import numeral from "numeral";
import * as R from "ramda";
import { set } from "lodash";

const NUMBER_FORMAT = "[0][0][0].[0]a";
export const leadingZeros = stringNumber => stringNumber.replace(/^0+(?!\.|$)/, "");

const FRAMES_RATE = 24;
const FRAMES_SECONDS = {
  IMAGE: 3,
  VIDEO: 5,
};

const FEATURE_REPLACEMENTS_TEXT = {
  LIKES_TOTAL: "likesTotal",
  LIKES_PER_POST: "likesPost",
  USERNAME: "username",
};

const setComposition = ({ composition, path, newValue }) => {
  if (path) {
    if (Array.isArray(path)) {
      path.forEach(element => {
        set(composition, element, newValue);
      });
    } else {
      set(composition, path, newValue);
    }
  }
};

const footageServerURL = ({ footage }) => {
  return footage.previewUri;
};

const completeAllNodes = ({
  nodeSets,
  footages,
  lastFootageIndex,
  useFootageRemoteUrl = false,
  texts,
  ...props
}) => {
  let nodes = [];
  let footageIndex = lastFootageIndex || 0;
  nodeSets.forEach(element => {
    element.nodes.forEach(node => {
      const composition = node.composition;
      node.layers.forEach((layer, index) => {
        if (layer.type == "footage") {
          const position = footageIndex + index;
          if (position <= footages.length - 1) {
            const footage = footages[position];
            setComposition({
              composition,
              path: layer.sourcePath,
              newValue: footageServerURL({ footage, useFootageRemoteUrl }),
            });
            setComposition({
              composition,
              path: layer.formatPath,
              newValue: "image/jpg",
            });
            setComposition({
              composition,
              path: layer.framesPath,
              newValue: FRAMES_SECONDS.IMAGE * FRAMES_RATE,
            });
          }
        } else if (layer.type === "text" && texts) {
          const textLayer = texts[layer.name];
          if (textLayer) set(composition, layer.text, textLayer);
        } else if (layer.type === "feature") {
          const newText = setFeatures({ layer, composition, footages, ...props });
          set(composition, layer.path, newText);
        } else {
          console.log("Layer empty value ", { layer });
        }
      });
      footageIndex += node.layers.filter(layer => layer.type === "footage").length;
      nodes = [...nodes, composition];
    });
  });
  return { nodes, lastFootageIndex: footageIndex };
};

const completePartialNodes = ({ nodeSets, footages, texts, ...props }) => {
  const [nodesMandatory, nodesNoMandatory] = R.partition(a => a.mandatory, nodeSets);
  const { nodes, lastFootageIndex } = completeAllNodes({
    nodeSets: nodesMandatory,
    footages,
    texts,
    ...props,
  });

  const noMandatoryNodes = completeAllNodes({
    nodeSets: nodesNoMandatory,
    footages,
    lastFootageIndex,
    texts,
  }).nodes;

  return [...nodes, ...noMandatoryNodes];
};

const setFeatures = ({ layer, totalLikes, footages, username, templateFeatures }) => {
  const defaultValue = layer.defaultValue;
  const groupId = layer.groupId;
  if (templateFeatures && templateFeatures[groupId] && !templateFeatures[groupId].active) {
    return layer.emptyValue;
  }

  var newText = defaultValue;
  const replacedUsernameText = `{{${FEATURE_REPLACEMENTS_TEXT.USERNAME}}}`;
  newText = defaultValue.replace(replacedUsernameText, username);
  const replacedTotalLikesText = `{{${FEATURE_REPLACEMENTS_TEXT.LIKES_TOTAL}}}`;
  newText = newText.replace(replacedTotalLikesText, totalLikes);

  var regexp = new RegExp(`{{${FEATURE_REPLACEMENTS_TEXT.LIKES_PER_POST}[1-9]}}`, "gi");
  const likesPerPost = defaultValue.match(regexp);
  if (likesPerPost && likesPerPost.length > 0) {
    const postNumber = likesPerPost[0]
      .replace(`{{${FEATURE_REPLACEMENTS_TEXT.LIKES_PER_POST}`, "")
      .replace("}}", "");
    const footageNumber = parseInt(postNumber);

    if (footageNumber <= footages.length) {
      const postLikes = footages[footageNumber - 1].likes;
      const postLikesNormalized = leadingZeros(numeral(postLikes).format(NUMBER_FORMAT));
      const replacedText = `{{${FEATURE_REPLACEMENTS_TEXT.LIKES_PER_POST}${footageNumber}}}`;
      newText = newText.replace(replacedText, postLikesNormalized);
    } else {
      newText = "";
    }
  }
  return newText;
};

const getPreviewJson = ({
  publicFootages,
  template,
  orientationVariant,
  texts,
  useFootageRemoteUrl = false,
  ...props
}) => {
  const variant = template ? template.variants[orientationVariant] : null;
  if (!variant) return new Error("Invalid orientation");
  const footageRequire = variant.requirements.footage;
  if (footageRequire.min > publicFootages.length || footageRequire.max < publicFootages.length) {
    console.log("[footages] Invalid", { footageRequire, l: publicFootages.length });
    return new Error("Invalid footages");
  }

  let nodes = [];
  const nodeSets = variant.nodeSets;
  if (footageRequire.max === publicFootages.length) {
    nodes = completeAllNodes({
      nodeSets,
      footages: publicFootages,
      texts,
      useFootageRemoteUrl,
      ...props,
    }).nodes;
  } else if (footageRequire.min === publicFootages.length) {
    nodes = completeAllNodes({
      nodeSets: nodeSets.filter(n => n.mandatory),
      footages: publicFootages,
      texts,
      useFootageRemoteUrl,
      ...props,
    }).nodes;
  } else {
    nodes = completePartialNodes({ nodeSets, footages: publicFootages, texts, ...props });
  }
  const compositionFinal = variant.composition;
  const finalNode = R.sortBy(R.prop("placement"), nodes);
  compositionFinal.nodes = finalNode;
  return compositionFinal;
};

const getComposition = ({ template, footages, texts, ...props }) => {
  const orientationVariant = "portrait";

  const variant = template && template.variants ? template.variants[orientationVariant] : null;
  if (!variant) return new Error("Invalid orientation");
  const footageRequire = variant.requirements.footage;
  let footagesMax = footageRequire.max;
  if (footageRequire.max > footages.length) {
    footagesMax = footages.length;
  }
  if (footageRequire.min > footages.length) {
    return new Error("Error creating video too many footages");
  }

  const publicFootages = footages.slice(0, footagesMax);
  const composition = getPreviewJson({
    publicFootages,
    template,
    orientationVariant,
    texts,
    ...props,
  });

  return composition;
};

export default getComposition;
